import React from 'react'
import PropTypes from 'prop-types'
import { graphql, StaticQuery } from 'gatsby'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import useIsHydrated from './Hooks/useIsHydrated'
import { annualFeeOptions } from './Utilities/DocumentToReactOptions'

function MembershipFee({
  annualMembershipFee,
  annualMembershipFeeYearTwo,
  monthlyMembershipFee,
  monthlyMembershipFeeYearTwo
}) {
  const sameAnnualMemFeeZeroMonthlyMemFee =
    annualMembershipFee === annualMembershipFeeYearTwo &&
    monthlyMembershipFee === 0 &&
    monthlyMembershipFeeYearTwo === 0
  const sameAnnualMemFeeNonzeroMonthlyMemFee =
    annualMembershipFee === annualMembershipFeeYearTwo && monthlyMembershipFee !== 0
  const differentAnnualMemFeeZeroMonthlyMemFee =
    annualMembershipFee !== annualMembershipFeeYearTwo &&
    monthlyMembershipFee === 0 &&
    monthlyMembershipFeeYearTwo === 0
  const nonzeroMonthlyMemFeeYearTwo = monthlyMembershipFeeYearTwo !== 0

  if (sameAnnualMemFeeZeroMonthlyMemFee) {
    return <strong>${annualMembershipFee}</strong>
  }
  if (sameAnnualMemFeeNonzeroMonthlyMemFee) {
    return (
      <>
        &nbsp;
        <strong>${annualMembershipFee}</strong> annually and billed monthly at&nbsp;
        <strong>${monthlyMembershipFee}</strong>.&nbsp;
      </>
    )
  }
  if (differentAnnualMemFeeZeroMonthlyMemFee) {
    return (
      <>
        Introductory fee of <strong>${annualMembershipFee}</strong> for the first year. After
        that,&nbsp;
        <strong>${annualMembershipFeeYearTwo}</strong> annually.
      </>
    )
  }
  if (nonzeroMonthlyMemFeeYearTwo) {
    return (
      <>
        Introductory fee of <strong>${annualMembershipFee}</strong> for the first year. After
        that,&nbsp;
        <strong>${annualMembershipFeeYearTwo}</strong> and billed monthly at&nbsp;
        <strong>${monthlyMembershipFeeYearTwo}</strong>.
      </>
    )
  }
  return null
}

MembershipFee.propTypes = {
  annualMembershipFee: PropTypes.number,
  annualMembershipFeeYearTwo: PropTypes.number,
  monthlyMembershipFee: PropTypes.number,
  monthlyMembershipFeeYearTwo: PropTypes.number
}

MembershipFee.defaultProps = {
  annualMembershipFee: 0,
  annualMembershipFeeYearTwo: 0,
  monthlyMembershipFee: 0,
  monthlyMembershipFeeYearTwo: 0
}

// Strategies priced under the increased fee schedule: a $41 maximum late fee and a 3% foreign
// transaction fee. Matched on Schumer ID because those terms live on the pricing strategy in
// avant-basic (CSRV-5841) and are not modelled in Contentful. If the content model gains
// lateFeeSubsequent and foreignTransactionFee (CSRV-5845), read them from
// cardPricingStrategyData instead and delete this list.
const PRICING_STRATEGY_CODES_FOR_INCREASED_FEES = [
  '0122',
  '0123',
  '3220',
  '3303',
  '5217',
  '7105',
  '7213',
  '9004'
]
// The box discloses the maximum late fee, which is avant-basic's subsequent-violation amount
// ("1st: up to $30 | Subsequent: up to $41") rather than the first-violation one.
const INCREASED_MAXIMUM_LATE_FEE = 41
const INCREASED_FOREIGN_TRANSACTION_FEE = 3

// The disclosure for a strategy with no configured fee, which is itself a disclosure.
function ForeignTransactionFee({ foreignTransactionFee }) {
  if (!foreignTransactionFee) {
    return <strong>None</strong>
  }

  return (
    <>
      <strong>{foreignTransactionFee}%</strong> of each foreign transaction in U.S. dollars.
    </>
  )
}

ForeignTransactionFee.propTypes = {
  foreignTransactionFee: PropTypes.number
}

ForeignTransactionFee.defaultProps = {
  foreignTransactionFee: null
}

function SchumerBox({ data }) {
  const isHydrated = useIsHydrated()
  // Returns null on first render, so the client and server match
  if (!isHydrated) return null
  return (
    <StaticQuery
      query={graphql`
        query {
          contentfulSeoMetadata(contentful_id: { eq: "KC2Syrm16SuPLMffleSVl" }) {
            primeRate
            aprCap
            lateFee
          }
        }
      `}
      render={staticData => {
        const { lateFee, primeRate, aprCap } = staticData.contentfulSeoMetadata
        const {
          isFixedApr,
          fixedApr,
          showAnnualFeeBox,
          annualMembershipFee,
          annualMembershipFeeYearTwo,
          monthlyMembershipFee,
          monthlyMembershipFeeYearTwo,
          overlimitFee,
          returnedPaymentFee,
          annualFeeNotice,
          isDailyBalance
        } = data.cardPricingStrategyData

        let annualFeeNoticeText
        if (annualFeeNotice) {
          const options = annualFeeOptions()
          annualFeeNoticeText = renderRichText(annualFeeNotice, options)
        }

        const calculateApr = () => {
          if (isFixedApr) {
            return fixedApr
          }
          return Math.min(primeRate + data.cardPricingStrategyData.marginRate, aprCap)
        }

        const balanceMethod = isDailyBalance ? 'Daily Balance Method' : 'Average Daily Balance'

        // A strategy outside the September 2026 set falls back to the global, so every page that
        // rendered the global before this change still renders it.
        const hasIncreasedFees = PRICING_STRATEGY_CODES_FOR_INCREASED_FEES.includes(
          data.schumerID?.trim()
        )
        const maximumLateFee = hasIncreasedFees ? INCREASED_MAXIMUM_LATE_FEE : lateFee
        const foreignTransactionFee = hasIncreasedFees ? INCREASED_FOREIGN_TRANSACTION_FEE : null

        const aprMessage = isFixedApr
          ? null
          : 'This APR will vary with the market based on the Prime Rate.'

        return (
          <main ref={null} id="maincontent" className="privacy-notice">
            <div className="schumer-container schumer legal container__inner">
              <h2
                className="schumer__header legal__heading"
                style={{
                  whiteSpace: 'nowrap',
                  maxWidth: 'unset',
                  minWidth: '100%',
                  color: 'white'
                }}
              >
                <style
                  dangerouslySetInnerHTML={{
                    __html: `.header-wrap { display: none;}
                    .footer-legal {display: none;}
                    #__segmentConsentManager {display: none;}`
                  }}
                />
                INTEREST RATES AND INTEREST CHARGES
              </h2>
              <table className="privacy-notice__table" border="1">
                <tbody>
                  <tr className="privacy-notice__row">
                    <th
                      scope="row"
                      className="privacy-notice__data schumer__data"
                      type="two-col-sm"
                    >
                      Annual Percentage Rate (APR) for Purchases
                    </th>
                    <td className="privacy-notice__data schumer__data">
                      <span className="apr">{calculateApr()}%</span>
                      <br />
                      <br />
                      {aprMessage}
                    </td>
                  </tr>

                  <tr className="privacy-notice__row">
                    <th
                      scope="row"
                      className="privacy-notice__data schumer__data"
                      type="two-col-sm"
                    >
                      APR for Cash Advances
                    </th>
                    <td className="privacy-notice__data schumer__data">
                      <strong>{calculateApr()}%</strong>
                      <br />
                      <br />
                      {aprMessage}
                    </td>
                  </tr>

                  <tr className="privacy-notice__row">
                    <th
                      scope="row"
                      className="privacy-notice__data schumer__data"
                      type="two-col-sm"
                    >
                      How to Avoid Paying Interest on Purchases
                    </th>
                    <td className="privacy-notice__data schumer__data">
                      Your due date is at least 24 days after the close of each billing period. We
                      will not charge you interest on purchases if you pay your entire balance by
                      the due date each month.
                    </td>
                  </tr>

                  <tr className="privacy-notice__row">
                    <th
                      scope="row"
                      className="privacy-notice__data schumer__data"
                      type="two-col-sm"
                    >
                      Minimum Interest Charge
                    </th>
                    <td className="privacy-notice__data schumer__data">
                      If you are charged interest, the charge will be no less than $1.00.
                    </td>
                  </tr>

                  <tr className="privacy-notice__row">
                    <th
                      scope="row"
                      className="privacy-notice__data schumer__data"
                      type="two-col-sm"
                    >
                      For Credit Card Tips from the Consumer Financial Protection Bureau
                    </th>
                    <td className="privacy-notice__data schumer__data">
                      <strong>
                        To learn more about factors to consider when applying for or using a credit
                        card, visit the website of the Consumer Financial Protection Bureau at{' '}
                        <a href="https://www.consumerfinance.gov/learnmore">
                          http://www.consumerfinance.gov/learnmore
                        </a>
                      </strong>
                    </td>
                  </tr>
                  <tr type="gap" />
                </tbody>
              </table>
              <h2
                className="schumer__header legal__heading"
                style={{
                  whiteSpace: 'nowrap',
                  maxWidth: 'unset',
                  minWidth: '100%',
                  color: 'white'
                }}
              >
                FEES
              </h2>
              <table className="privacy-notice__table" border="1">
                <tbody>
                  <tr className="privacy-notice__row">
                    <th
                      scope="row"
                      className="privacy-notice__data schumer__data"
                      type="two-col-sm"
                    >
                      {showAnnualFeeBox ? 'Annual Fee' : 'Monthly Membership Fee'}
                    </th>

                    <td className="privacy-notice__data schumer__data">
                      <MembershipFee
                        annualMembershipFee={annualMembershipFee}
                        annualMembershipFeeYearTwo={annualMembershipFeeYearTwo}
                        monthlyMembershipFee={monthlyMembershipFee}
                        monthlyMembershipFeeYearTwo={monthlyMembershipFeeYearTwo}
                      />
                      <br />
                      <br />
                      {annualFeeNoticeText}
                    </td>
                  </tr>
                  <tr className="privacy-notice__row">
                    <th
                      scope="row"
                      className="privacy-notice__data schumer__data"
                      type="two-col-sm"
                    >
                      Transaction Fees
                      <ul className="privacy-notice__list">
                        <li className="unbold">Cash Advance</li>
                        <li className="unbold">Foreign Transaction</li>
                      </ul>
                    </th>
                    <td className="privacy-notice__data schumer__data">
                      <p className="privacy-notice__text">
                        <ul className="privacy-notice__list">
                          <li style={{ listStyleType: 'none' }}>&nbsp;</li>
                          <li>
                            The greater of <strong>$10</strong> or <strong>3%</strong> of the amount
                            of the cash advance.
                          </li>
                          <li>
                            <ForeignTransactionFee foreignTransactionFee={foreignTransactionFee} />
                          </li>
                        </ul>
                      </p>
                    </td>
                  </tr>
                  <tr className="privacy-notice__row">
                    <th
                      scope="row"
                      className="privacy-notice__data schumer__data"
                      type="two-col-sm"
                    >
                      Penalty Fees
                      <ul className="privacy-notice__list">
                        <li className="unbold">Late Fee</li>
                        {returnedPaymentFee && <li className="unbold">Returned Payment Fee</li>}
                        {overlimitFee && <li className="unbold">Overlimit Fee</li>}
                      </ul>
                    </th>
                    <td className="privacy-notice__data schumer__data">
                      <p className="privacy-notice__text">
                        <ul className="privacy-notice__list">
                          <li style={{ listStyleType: 'none' }}>&nbsp;</li>
                          <li>
                            Up to <strong>${maximumLateFee}</strong>
                          </li>
                          {returnedPaymentFee && (
                            <li>
                              Up to <strong>${returnedPaymentFee}</strong>&nbsp;
                            </li>
                          )}
                          {overlimitFee ? (
                            <li>
                              {overlimitFee !== 'None' ? (
                                <>
                                  Up to <strong>${overlimitFee}</strong>
                                </>
                              ) : (
                                <strong>None</strong>
                              )}
                            </li>
                          ) : null}
                        </ul>
                      </p>
                    </td>
                  </tr>
                  <tr type="gap" />
                </tbody>
              </table>
              <div className="footer-legal__text-box">
                <p className="footer-legal__text">
                  <strong>How We Will Calculate Your Balance:</strong> We use a method called “
                  {balanceMethod} (including new transactions).”
                  <br />
                  <br />
                  For Information About This Offer: Contact us at (855) 752-7012. New York residents
                  may contact the New York State Department of Financial Services for free
                  information on comparative credit card rates, fees, and grace periods at
                  800-342-3736 or{' '}
                  <a href="https://www.dfs.ny.gov/consumers/credit_debt">
                    https://www.dfs.ny.gov/consumers/credit_debt
                  </a>
                  .
                  <br />
                  <br />
                  This product is offered by WebBank, and serviced by Avant, LLC. WebBank is located
                  in Salt Lake City, Utah.
                  <br />
                  <br />
                  To receive a credit card, you must meet our applicable criteria bearing on
                  creditworthiness, including adequate income. The minimum credit limit will be
                  $300.
                  <br />
                  <br />
                  If an account is opened, you will receive a Cardmember Agreement with your card.
                  The Cardmember Agreement includes an arbitration clause with a class action and
                  jury trial waiver.
                  <br />
                  <br />
                  California Residents: If you are married, you may apply for a separate account.
                  <br />
                  <br />
                  Ohio Residents: The Ohio laws against discrimination require that all creditors
                  make credit equally available to all creditworthy customers, and that credit
                  reporting agencies maintain separate credit histories on each individual upon
                  request. The Ohio Civil Rights Commission administers compliance with this law.
                  <br />
                  <br />
                  New York, Rhode Island and Vermont Residents: You understand and agree that we may
                  obtain a consumer report in connection with this application and in connection
                  with any update, renewals or extension of any credit as a result of this
                  application. Upon your request, we will tell you the name and address of the
                  consumer reporting agency. By submitting an application, you consent to our
                  obtaining such consumer credit reports.{' '}
                </p>
              </div>
            </div>
          </main>
        )
      }}
    />
  )
}

SchumerBox.propTypes = {
  data: PropTypes.object
}

SchumerBox.defaultProps = {
  data: {}
}

export default SchumerBox
